import { useEffect, useState } from 'react';
import NudosSearchbarDropdown from '../../../../../../../../components/NudosComponents/NudosSearchbarDropdown/NudosSearchbarDropdown';
import { IToolData } from '../../../../../../../../types/products';
import { displayErrorNotification } from '../../../../../../../../utils/displayNudosStandardNotifications';
import { fetchReplacementTools } from '../../../../../../../../services/services';
import { IfetchReplacementTools } from '../../../../../../../../types/services';
import { filterPillsToShow } from '../../../../../../../../utils/pillsFormat';
import { NudosHoverText } from '../../../../../../../../components/NudosComponents';
import { IpillsByReference } from '../../../../../../../../types/orders';
import './ReplacementToolsDropdown.scss';

const ReplacementToolsDropdown = ({
  replacementToolsSearchOptions,
  updateSelectedToolCallback,
  originalToolId
}: IReplacementToolsDropdownProps) => {
  const [replacementTools, setReplacementTools] = useState<IToolData[]>([]);
  const [selectedTool, setSelectedTool] = useState<IToolData>();
  const [loadingReplacementTools, setLoadingReplacementTools] = useState<boolean>(false);
  const { place, category, countryCode, organizationId, userId, referenceModel, search } =
    replacementToolsSearchOptions || {};

  const formSelectedOptionUICallback = (rawSelectedOption: IToolData) => {
    const text = `${rawSelectedOption?.name} - ${rawSelectedOption?.serial}`;
    return (
      <div className="selectedOption" onClick={() => setSelectedTool(rawSelectedOption)}>
        <NudosHoverText text={text} onlyIfTruncated charactersLimit={43} />
      </div>
    );
  };

  const formatPills = (pills?: IpillsByReference | string) => {
    const parsedPills = typeof pills === 'string' ? JSON.parse(pills || '{}') : pills;
    return filterPillsToShow(parsedPills);
  };

  const filterBySearchStringCallback = (search: string, rawOptions: IToolData[]) => {
    return rawOptions.filter(option => {
      const optionPills = formatPills(option?.pills);
      return (
        option?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()) ||
        option?.serial?.toLowerCase().trim().includes(search.toLowerCase().trim()) ||
        optionPills?.some(pill => pill.toLowerCase().trim().includes(search.toLowerCase().trim()))
      );
    });
  };

  const formDropdownOptionsCallback = (rawOptions: IToolData[], clickOptionCallback?: (option: IToolData) => void) => {
    const noOptionsText = 'No hay seriales en Bodegas que coincidan con tu búsqueda';
    if (!rawOptions?.length)
      return [
        <div className="optionContainer dropdownItem noOptions" key="noOptions">
          {noOptionsText}
        </div>
      ];
    return rawOptions.map((option: IToolData, i) => {
      const handleSelectOption = () => {
        setSelectedTool(option);
        clickOptionCallback?.(option);
      };
      const pillsToShow = formatPills(option?.pills);
      return (
        <div className="optionContainer dropdownItem" key={`option${option?.id}${i}`} onClick={handleSelectOption}>
          <div className="productName">{option?.name}</div>
          <div className="productCharacteristics">
            {!!pillsToShow?.length && (
              <NudosHoverText text={`${pillsToShow?.join(' - ')} |`} onlyIfTruncated charactersLimit={35} />
            )}
            <NudosHoverText text={`${option?.serial || 'Sin serial'}`} onlyIfTruncated charactersLimit={20} />
          </div>
        </div>
      );
    });
  };

  const getReplacementTools = async () => {
    if (!place || !countryCode || !category || !organizationId) return;
    setLoadingReplacementTools(true);
    try {
      const replacementToolsResponse = await fetchReplacementTools({
        place,
        countryCode,
        category,
        organizationId,
        userId,
        referenceModel,
        search
      });
      const filteredReplacementTools = replacementToolsResponse.filter(
        tool => !originalToolId || tool.id !== originalToolId
      );
      setReplacementTools(filteredReplacementTools);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingReplacementTools(false);
    }
  };

  useEffect(() => {
    getReplacementTools();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    selectedTool && updateSelectedToolCallback(selectedTool);
  }, [selectedTool]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="replacementToolsDropdown">
      <NudosSearchbarDropdown
        label="Selecciona la herramienta"
        rawOptions={replacementTools}
        formDropdownOptionsCallback={formDropdownOptionsCallback}
        formSelectedOptionUICallback={formSelectedOptionUICallback}
        filterBySearchStringCallback={filterBySearchStringCallback}
        includeMagnifierIcon={false}
        customPlaceholder="Busca o selecciona otra herramienta"
        customClassname="customSelect"
        isDebouce
        componentSize="medium"
        currentValue={selectedTool}
        showSkeletonLoader={loadingReplacementTools}
        isFilled={!!selectedTool}
        isDeactivated={!replacementTools?.length}
        caption={!replacementTools?.length ? 'No hay seriales disponibles en bodega para este SKU' : undefined}
      />
    </div>
  );
};

export default ReplacementToolsDropdown;

interface IReplacementToolsDropdownProps {
  replacementToolsSearchOptions: Partial<IfetchReplacementTools>;
  updateSelectedToolCallback: (tool: IToolData) => void;
  originalToolId?: number;
}
